import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useSiteText } from '@/lib/SiteTextContext';
import { useSiteImages } from '@/lib/SiteImagesContext';

export function Navbar() {
  const { t } = useSiteText();
  const { img } = useSiteImages();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);

    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }

    setMobileOpen(false);
  };

  const navItems = [
    { label: t('about.label'), id: 'about' },
    { label: t('sailing.label'), id: 'sailing' },
    { label: t('news.label'), id: 'news-carousel' },
    { label: t('lake.label'), id: 'lake' },
    { label: t('gallery.label'), id: 'gallery' },
    { label: t('contact.label'), id: 'contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-2xl border-b border-gray-200/50 shadow-lg h-16'
          : 'bg-transparent h-20'
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 lg:px-24 h-full">
        {/* Logo */}
        <button
          onClick={() => scrollTo('hero')}
          className="group flex items-center transition-transform duration-300 ease-sail hover:scale-105 active:scale-95"
          aria-label="ChKŻ LOK — strona główna"
        >
          <img
            src={img('logo')}
            alt="Chodzieski Klub Żeglarski LOK"
            className={`h-10 w-auto transition-all duration-500 ease-sail ${
              scrolled ? '' : 'brightness-0 invert'
            }`}
          />
        </button>

        {/* Desktop Navigation */}
        <div className="hidden xl:flex items-center gap-6">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`relative text-sm font-medium link-underline transition-colors duration-300 ${
                scrolled
                  ? 'text-gray-700 hover:text-ocean-blue'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`xl:hidden w-10 h-10 flex items-center justify-center transition-colors duration-300 ${
            scrolled ? 'text-gray-700' : 'text-white'
          }`}
          aria-label="Menu"
          aria-expanded={mobileOpen}
        >
          <span className="relative w-6 h-6 flex items-center justify-center">
            <Menu
              size={24}
              className={`absolute transition-all duration-300 ${
                mobileOpen ? 'opacity-0 rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'
              }`}
            />
            <X
              size={24}
              className={`absolute transition-all duration-300 ${
                mobileOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-50'
              }`}
            />
          </span>
        </button>
      </div>

      {/* Mobile menu — smooth open/close */}
      <div
        className={`xl:hidden overflow-hidden transition-all duration-500 ease-sail ${
          mobileOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white/95 backdrop-blur-xl border-t border-gray-200/50 shadow-lg">
          <div className="flex flex-col px-6 py-6 gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="text-sm font-medium text-gray-700 hover:text-ocean-blue transition-colors duration-300 text-left py-2.5 border-b border-gray-100"
              >
                {item.label}
              </button>
            ))}

            <button
              onClick={() => scrollTo('contact')}
              className="mt-4 text-sm font-bold text-center text-ocean-blue bg-accent-gold hover:bg-accent-gold-dark rounded-full px-6 py-3 transition-all duration-300 btn-shimmer"
            >
              {t('hero.cta2')}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}